import { Link } from "react-router-dom";
import {
  Band,
  Button,
  Hero,
  Note,
  Wrap,
  coverFrame,
  lede,
  sectionHead,
} from "../components/ui/index.ts";
import {
  amazonUrl,
  booksInLine,
  COVER_WIDTHS,
  coverSrc,
  coverSrcSet,
  LINES,
} from "../data/books.ts";

/**
 * The book list, kept off the front page on purpose. A catalogue grows; a front
 * page that grows with it would slowly turn the company back into whichever
 * product line happened to be first.
 *
 * Each line heads its own section and links to its own page, because what a
 * line guarantees is a fact about that line and not about the press.
 */
export default function Catalogue() {
  return (
    <>
      <Hero
        eyebrow="Everything in print"
        title="Catalogue"
        lede="Short, on purpose. A book only joins the list once we have checked it."
        emblem="small"
      />

      {LINES.map((line) => (
        <Band key={line.id}>
          <Wrap>
            {/* The line names its own section, so it is the h2 — the book
                titles below are h3. It used to be a kicker inside a <p>, which
                left this page running h1 straight to h3 with no h2 anywhere. */}
            <h2
              className={`${sectionHead} [&_a]:text-inherit [&_a]:no-underline [&_a:hover]:text-rust [&_a:hover]:underline [&_a:hover]:underline-offset-[4px]`}
            >
              <Link to={line.href}>{line.name} →</Link>
            </h2>
            <p className={`${lede} mb-[1.6rem]`}>{line.summary}</p>

            <ul className="list-none m-0 p-0 border-t-2 border-ink">
              {booksInLine(line.id).map((book) => (
                <li
                  key={book.slug}
                  className="border-b border-line py-[1.6rem] grid gap-4 gap-x-[clamp(1.5rem,4vw,3rem)] split:grid-cols-[7rem_minmax(0,1fr)_13rem] split:items-start"
                >
                  {/* The cover links to the same page as the title. Hidden from
                      the accessibility tree rather than given its own alt, so a
                      screen reader is not read the same book twice in a row. */}
                  <Link
                    className="block w-[6rem] split:w-full"
                    to={`/catalogue/${book.slug}`}
                    aria-hidden
                    tabIndex={-1}
                  >
                    <img
                      className={coverFrame}
                      src={coverSrc(book, COVER_WIDTHS[0])}
                      srcSet={coverSrcSet(book)}
                      // 760px is `--breakpoint-split`, spelled out because a
                      // `sizes` attribute is HTML and cannot read a CSS token.
                      // Moving the breakpoint means moving this too.
                      sizes="(min-width: 760px) 7rem, 6rem"
                      width={COVER_WIDTHS[0]}
                      height={Math.round((COVER_WIDTHS[0] * 110) / 85)}
                      alt=""
                      loading="lazy"
                    />
                  </Link>
                  <div>
                    <span className="inline-block text-[0.7rem] font-bold tracking-[0.14em] uppercase text-rust">
                      {book.designation}
                    </span>
                    <h3 className="font-display text-[clamp(1.35rem,3.4vw,1.7rem)] mt-[0.35rem] mb-[0.5rem] leading-[1.15] [&_a]:text-inherit [&_a]:no-underline [&_a:hover]:text-rust [&_a:hover]:underline [&_a:hover]:decoration-[1px] [&_a:hover]:underline-offset-[4px]">
                      <Link to={`/catalogue/${book.slug}`}>
                        {book.title}
                        {book.subtitle ? <>: {book.subtitle}</> : null}
                      </Link>
                    </h3>
                    <p className="text-muted text-minor max-w-measure mb-0">{book.blurb}</p>
                  </div>
                  <div className="flex flex-col items-start gap-3">
                    <p className="text-[0.85rem] text-muted uppercase tracking-[0.1em] m-0">
                      {book.puzzleCount} puzzles
                    </p>
                    <Button to={`/catalogue/${book.slug}`} ghost>
                      Details
                    </Button>
                    {/* Archived books that KDP has not listed yet have no link. */}
                    {amazonUrl(book) ? (
                      <Button href={amazonUrl(book)!} rel="noreferrer noopener">
                        Find it on Amazon
                      </Button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </Wrap>
        </Band>
      ))}

      <Band variant="alt">
        <Wrap>
          <Note>
            <p className="mb-0">
              <strong>More on the way.</strong> The line runs from Introduction up through Expert
              and three tiers past that, in six flavours each — from cozy everyday scenes to murder
              mysteries, heists and spy rings.
            </p>
          </Note>
        </Wrap>
      </Band>
    </>
  );
}
